import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useUpdateProfileMutation, useDeletePhotoMutation } from "../api";
import { useNotification } from "../hooks/useNotification";
import { useValidationErrors } from "../hooks/useValidationErrors";
import Notification from "../components/ui/Notification";
import FieldError from "../components/ui/FieldError";
import { useTranslation } from "react-i18next";

const ProfilePage: React.FC = () => {
    const { t } = useTranslation(["common", "dashboard"]);
    const { user } = useSelector((state: RootState) => state.auth);
    const [updateProfile, { isLoading }] = useUpdateProfileMutation();
    const [deletePhoto, { isLoading: isDeletingPhoto }] =
        useDeletePhotoMutation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { getFieldError, hasFieldError, setErrors, clearErrors } =
        useValidationErrors();

    const [formData, setFormData] = useState({
        name: user?.name || "",
        surname: user?.surname || "",
        email: user?.email || "",
        bank_account: user?.bank_account || "",
        bank_swift: user?.bank_swift || "",
        bank_card: user?.bank_card || "",
        bep20: user?.bep20 || "",
        trc20: user?.trc20 || "",
        address: user?.address || "",
        heir_name: user?.heir_name || "",
        heir_surname: user?.heir_surname || "",
        telegram_username: user?.telegram_username || "",
        telegram_bot: user?.telegram_bot || "",
        instagram: user?.instagram || "",
        facebook: user?.facebook || "",
    });

    const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const { notification, showSuccess, showError, hideNotification } =
        useNotification();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Очищаем ошибку для этого поля при изменении
        if (hasFieldError(name)) {
            setErrors(null);
        }
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Проверяем размер файла (2MB = 2 * 1024 * 1024 bytes)
            if (file.size > 2 * 1024 * 1024) {
                showError(t("common.file_too_large", { ns: "common" }));
                return;
            }

            setSelectedPhoto(file);

            // Создаем превью
            const reader = new FileReader();
            reader.onload = (e) => {
                setPhotoPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePhoto = () => {
        setSelectedPhoto(null);
        setPhotoPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDeletePhoto = async () => {
        if (!user?.photo) {
            showError(t("common.photo_not_found", { ns: "common" }));
            return;
        }

        if (
            !window.confirm(t("common.delete_photo_confirm", { ns: "common" }))
        ) {
            return;
        }

        try {
            await deletePhoto().unwrap();
            showSuccess(t("common.photo_deleted", { ns: "common" }));
            // Обновляем состояние пользователя (можно добавить обновление через Redux)
            // Пока просто показываем успешное сообщение
        } catch (error: any) {
            console.error("Ошибка при удалении фото:", error);
            if (error?.message) {
                showError(error.message);
            } else {
                showError(t("common.error_deleting_photo", { ns: "common" }));
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            // Очищаем предыдущие ошибки
            clearErrors();

            const updateData: any = { ...formData };

            // Добавляем фото только если оно выбрано
            if (selectedPhoto) {
                updateData.photo = selectedPhoto;
            }

            await updateProfile(updateData).unwrap();
            showSuccess(t("common.success", { ns: "common" }));
            setIsEditing(false);
            setSelectedPhoto(null);
            setPhotoPreview(null);
        } catch (error: any) {
            console.error("Ошибка при обновлении профиля:", error);
            // Проверяем, является ли ошибка ошибкой валидации
            if (error?.errors) {
                setErrors(error as any);
                showError(
                    error.message ||
                        t("common.validation_error", { ns: "common" })
                );
            } else {
                showError(t("common.error_updating_profile", { ns: "common" }));
            }
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        clearErrors();
        setFormData({
            name: user?.name || "",
            surname: user?.surname || "",
            email: user?.email || "",
            bank_account: user?.bank_account || "",
            bank_swift: user?.bank_swift || "",
            bank_card: user?.bank_card || "",
            bep20: user?.bep20 || "",
            trc20: user?.trc20 || "",
            address: user?.address || "",
            heir_name: user?.heir_name || "",
            heir_surname: user?.heir_surname || "",
            telegram_username: user?.telegram_username || "",
            telegram_bot: user?.telegram_bot || "",
            instagram: user?.instagram || "",
            facebook: user?.facebook || "",
        });
        setSelectedPhoto(null);
        setPhotoPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div className="space-y-6">
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t("common.title", { ns: "common" })}
                    </h2>
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 rounded-md"
                    >
                        {isEditing
                            ? t("common.cancel", { ns: "common" })
                            : t("common.edit", { ns: "common" })}
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Личная информация */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                            {t("common.personal_info", { ns: "common" })}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.name", { ns: "common" })} *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    required
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("name")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError error={getFieldError("name")} />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.surname", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="surname"
                                    value={formData.surname}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("surname")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError error={getFieldError("surname")} />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.email", { ns: "common" })} *
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    required
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("email")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError error={getFieldError("email")} />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.address", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("address")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError error={getFieldError("address")} />
                            </div>
                        </div>
                    </div>

                    {/* Фото профиля */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                            {t("common.photo", { ns: "common" })}
                        </h3>
                        <div className="flex items-center space-x-6">
                            <div className="flex-shrink-0">
                                {(photoPreview || user?.photo) && (
                                    <img
                                        src={photoPreview || user?.photo}
                                        alt="Profile"
                                        className="h-20 w-20 rounded-full object-cover"
                                    />
                                )}
                                {!photoPreview && !user?.photo && (
                                    <div className="h-20 w-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                        <svg
                                            className="h-8 w-8 text-gray-400 dark:text-gray-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                            />
                                        </svg>
                                    </div>
                                )}
                            </div>

                            <div className="flex-1">
                                {isEditing ? (
                                    <>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoChange}
                                            className="hidden"
                                        />
                                        <div className="flex space-x-3">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    fileInputRef.current?.click()
                                                }
                                                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md"
                                            >
                                                {t("common.select_photo", {
                                                    ns: "common",
                                                })}
                                            </button>
                                            {selectedPhoto && (
                                                <button
                                                    type="button"
                                                    onClick={handleRemovePhoto}
                                                    className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md"
                                                >
                                                    {t("common.remove_photo", {
                                                        ns: "common",
                                                    })}
                                                </button>
                                            )}
                                            {/* Показываем кнопку удаления существующего фото в режиме редактирования */}
                                            {user?.photo && !selectedPhoto && (
                                                <button
                                                    type="button"
                                                    onClick={handleDeletePhoto}
                                                    disabled={isDeletingPhoto}
                                                    className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md disabled:opacity-50"
                                                >
                                                    {isDeletingPhoto
                                                        ? t("common.loading", {
                                                              ns: "common",
                                                          })
                                                        : t(
                                                              "common.delete_photo",
                                                              { ns: "common" }
                                                          )}
                                                </button>
                                            )}
                                        </div>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            {t("common.photo_help", {
                                                ns: "common",
                                            })}
                                        </p>
                                    </>
                                ) : (
                                    // В режиме просмотра не показываем никаких кнопок для работы с фото
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {t("common.photo_view_mode", {
                                            ns: "common",
                                        })}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Банковская информация */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                            {t("common.banking_info", { ns: "common" })}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.bank_account", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="bank_account"
                                    value={formData.bank_account}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("bank_account")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError
                                    error={getFieldError("bank_account")}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.bank_swift", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="bank_swift"
                                    value={formData.bank_swift}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("bank_swift")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError
                                    error={getFieldError("bank_swift")}
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.bank_card", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="bank_card"
                                    value={formData.bank_card}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("bank_card")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError
                                    error={getFieldError("bank_card")}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Криптовалютные кошельки */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                            {t("common.crypto_info", { ns: "common" })}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.bep20", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="bep20"
                                    value={formData.bep20}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("bep20")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError error={getFieldError("bep20")} />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.trc20", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="trc20"
                                    value={formData.trc20}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("trc20")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError error={getFieldError("trc20")} />
                            </div>
                        </div>
                    </div>

                    {/* Информация о наследнике */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                            {t("common.heir_info", { ns: "common" })}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.heir_name", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="heir_name"
                                    value={formData.heir_name}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("heir_name")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError
                                    error={getFieldError("heir_name")}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.heir_surname", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="heir_surname"
                                    value={formData.heir_surname}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("heir_surname")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError
                                    error={getFieldError("heir_surname")}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Социальные сети */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                            {t("common.social_info", { ns: "common" })}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.telegram_username", {
                                        ns: "common",
                                    })}
                                </label>
                                <input
                                    type="text"
                                    name="telegram_username"
                                    value={formData.telegram_username}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("telegram_username")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError
                                    error={getFieldError("telegram_username")}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.telegram_bot", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="telegram_bot"
                                    value={formData.telegram_bot}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("telegram_bot")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError
                                    error={getFieldError("telegram_bot")}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.instagram", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="instagram"
                                    value={formData.instagram}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("instagram")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError
                                    error={getFieldError("instagram")}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t("common.facebook", { ns: "common" })}
                                </label>
                                <input
                                    type="text"
                                    name="facebook"
                                    value={formData.facebook}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                                        hasFieldError("facebook")
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    }`}
                                />
                                <FieldError error={getFieldError("facebook")} />
                            </div>
                        </div>
                    </div>

                    {/* Кнопки действий */}
                    {isEditing && (
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
                            >
                                {t("common.cancel", { ns: "common" })}
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 rounded-md disabled:opacity-50"
                            >
                                {isLoading
                                    ? t("common.saving", { ns: "common" })
                                    : t("common.save", { ns: "common" })}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ProfilePage;
